import { useQuery } from '@tanstack/react-query';
import { ArrowUp, RotateCcw, Sparkles, Square, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { Markdown } from '@/modules/docs/Markdown';
import { askJuni, juniAvailable, MAX_QUESTION_CHARS, type JuniAudience, type JuniTurn } from '@/modules/juni/juni-api';
import { useJuniStore, type JuniMessage } from '@/modules/juni/juni-store';

/**
 * Juni, the assistant: a button in the corner and the conversation it opens.
 *
 * ## What the reader sees
 *
 * Three dots while it works, then the answer — and nothing under it. The model runs on the
 * platform's own CPU and an answer can take tens of seconds, but narrating that ("reading the
 * documentation", a count of seconds) shows the reader the machinery and makes the wait feel
 * longer. A "Pages used" list went the same way on 2026-09-23 at the owner's request: shown before
 * the answer, then after it, and wanted in neither place. The sections a question matches are the
 * model's reading, not a reading list — "which Linux systems are supported?" listed Install Certbot.
 *
 * ## Nothing is kept
 *
 * The conversation lives in memory for the tab (see juni-store) and the last two exchanges go with
 * each question so a follow-up makes sense. Closing the tab forgets it.
 */

/** Earlier turns sent with a question: two exchanges, the same as the server keeps. */
const HISTORY_TURNS = 4;

/** An answer being written, per conversation, so Stop and New chat can end it. */
const running = new Map<JuniAudience, AbortController>();

const INTRO: Record<JuniAudience, string> = {
  visitor:
    'I answer questions about Techie Gain: what it does, its playbooks and templates, monitoring, backups and plans.',
  customer:
    'I answer from the documentation and from your own account: servers, monitors, backups, runs and subscription. I can read your account, but I cannot change anything in it.',
};

const SUGGESTIONS: Record<JuniAudience, string[]> = {
  visitor: ['What does Techie Gain do?', 'How much does it cost?', 'Which Linux systems are supported?'],
  customer: ['Do any of my servers need attention?', 'How do I set up backups?', 'How do I give a colleague sudo access?'],
};

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** The finished exchanges, as the server takes them: each question with the answer it got. */
function historyOf(messages: JuniMessage[]): JuniTurn[] {
  const turns: JuniTurn[] = [];

  messages.forEach((message, index) => {
    const question = messages[index - 1];

    if (message.role === 'assistant' && !message.pending && message.text.trim() !== '' && question?.role === 'user') {
      turns.push({ role: 'user', content: question.text }, { role: 'assistant', content: message.text });
    }
  });

  return turns.slice(-HISTORY_TURNS);
}

/** What went wrong, in the reader's terms. */
function describeFailure(error: unknown, audience: JuniAudience): string {
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return error.problem.detail || 'That is a lot of questions in a short time. Try again later.';
    }

    if (error.status === 422) {
      return error.fieldErrors.question?.[0] ?? error.message;
    }

    if (error.status === 401 && audience === 'customer') {
      return 'Your session has ended. Sign in again to ask Juni.';
    }
  }

  return 'Juni could not be reached. Check your connection and try again in a minute.';
}

/**
 * The answer's Markdown, made safe to show: every link becomes its own text, `*` bullets become
 * the `-` the renderer reads, and an em dash becomes a comma.
 *
 * Juni is told not to write links. One it writes anyway was composed, not read: it may point
 * anywhere, and the ones seen in testing were plausible paths that do not exist. The words stay;
 * only the link goes.
 *
 * The dash is the model's habit, and the owner reads it as a placeholder (2026-09-25). Nothing else
 * in the product uses one, and a comma carries the same pause in almost every sentence the model
 * writes. Spaces only, never a line break, so a line is never joined to the one before it.
 */
function answerMarkdown(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)\s]*\)/g, '$1')
    .replace(/^(\s*)\* /gm, '$1- ')
    .replace(/[ \t]*—[ \t]*/g, ', ');
}

/**
 * Three dots, and after a long wait a plain "still writing".
 *
 * It says nothing about what Juni is doing. The first version narrated the machinery — reading the
 * documentation, how many seconds it had taken — which reads as an excuse, makes the wait feel
 * longer than it is, and tells a customer about the inside of the product rather than answering
 * them. A person typing shows three dots.
 */
function Waiting({ startedAt }: { startedAt: number }) {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), Math.max(0, startedAt + 20_000 - Date.now()));
    return () => window.clearTimeout(timer);
  }, [startedAt]);

  return (
    <p className="text-muted-foreground flex items-center gap-2 text-sm" aria-label="Juni is writing">
      <span className="flex items-center gap-1" aria-hidden="true">
        {[0, 1, 2].map((dot) => (
          <span
            key={dot}
            className="bg-muted-foreground/70 size-1.5 animate-pulse rounded-full"
            style={{ animationDelay: `${dot * 0.18}s`, animationDuration: '1.1s' }}
          />
        ))}
      </span>
      {slow && <span>Still writing…</span>}
    </p>
  );
}

function Answer({ message }: { message: JuniMessage }) {
  const waiting = message.pending && message.text === '' && message.notice === null && message.error === null;

  return (
    <div className="space-y-3" aria-busy={message.pending}>
      {waiting && <Waiting startedAt={message.startedAt} />}
      {message.text !== '' && <Markdown source={answerMarkdown(message.text)} compact />}
      {message.notice !== null && <p className="text-foreground text-sm">{message.notice}</p>}
      {message.error !== null && <p className="text-danger text-sm">{message.error}</p>}
    </div>
  );
}

export function JuniWidget({ audience }: { audience: JuniAudience }) {
  const open = useJuniStore((state) => state.open[audience]);
  const messages = useJuniStore((state) => state.messages[audience]);
  const { setOpen, add, update, clear } = useJuniStore.getState();

  // Asked once for the tab: an installation can be running without a model server. A request that
  // fails is asked again, with growing pauses, so a page loaded mid-deploy still gets its button.
  const { data: available } = useQuery({
    queryKey: ['juni', 'available'],
    queryFn: juniAvailable,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 4,
    retryDelay: (attempt) => Math.min(30_000, 2_000 * 2 ** attempt),
  });

  const [draft, setDraft] = useState('');
  const inputId = useId();
  const input = useRef<HTMLTextAreaElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const wasOpen = useRef(open);

  const busy = messages.some((message) => message.pending);

  useEffect(() => {
    if (open) {
      input.current?.focus();
    } else if (wasOpen.current) {
      launcher.current?.focus();
    }

    wasOpen.current = open;
  }, [open]);

  // Keep the newest words in view while the answer is written, unless the reader has scrolled up.
  useEffect(() => {
    const element = scroller.current;

    if (element !== null && following.current) {
      element.scrollTop = element.scrollHeight;
    }
  }, [messages, open]);

  async function send(question: string): Promise<void> {
    const text = question.trim();

    if (text === '' || busy) return;

    const history = historyOf(useJuniStore.getState().messages[audience]);
    const answerId = newId();
    const controller = new AbortController();

    add(audience, { id: newId(), role: 'user', text, notice: null, error: null, pending: false, startedAt: Date.now() });
    add(audience, { id: answerId, role: 'assistant', text: '', notice: null, error: null, pending: true, startedAt: Date.now() });
    setDraft('');
    following.current = true;
    running.set(audience, controller);

    try {
      await askJuni({
        audience,
        question: text,
        history,
        signal: controller.signal,
        onEvent: (event) => {
          switch (event.type) {
            case 'text':
              update(audience, answerId, (message) => ({ text: message.text + event.text }));
              break;
            case 'notice':
              update(audience, answerId, () => ({ notice: event.text }));
              break;
            case 'error':
              update(audience, answerId, () => ({ error: event.text }));
              break;
            case 'done':
              break;
          }
        },
      });
    } catch (error) {
      if (controller.signal.aborted) {
        update(audience, answerId, (message) => ({ error: message.text === '' ? 'Stopped before an answer was written.' : 'Stopped.' }));
      } else {
        update(audience, answerId, () => ({ error: describeFailure(error, audience) }));
      }
    } finally {
      update(audience, answerId, () => ({ pending: false }));

      if (running.get(audience) === controller) running.delete(audience);
    }
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    void send(draft);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send(draft);
    }
  }

  function startOver(): void {
    running.get(audience)?.abort();
    clear(audience);
    setDraft('');
    input.current?.focus();
  }

  if (available !== true) {
    return null;
  }

  if (!open) {
    return (
      <button
        ref={launcher}
        type="button"
        onClick={() => setOpen(audience, true)}
        className="bg-primary text-primary-foreground shadow-glow focus-visible:ring-ring fixed right-4 bottom-4 z-40 inline-flex h-12 items-center gap-2 rounded-full px-4 text-sm font-medium transition hover:brightness-110 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:right-6 sm:bottom-6"
      >
        <Sparkles className="size-4" aria-hidden="true" />
        Ask Juni
      </button>
    );
  }

  const remaining = MAX_QUESTION_CHARS - draft.length;

  return (
    <section
      role="dialog"
      aria-label="Juni, the Techie Gain assistant"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(audience, false);
      }}
      className="surface fixed inset-0 z-[60] flex flex-col overflow-hidden shadow-2xl sm:inset-auto sm:right-6 sm:bottom-6 sm:h-[min(640px,calc(100vh-3rem))] sm:w-[400px] sm:rounded-2xl"
    >
      <header className="border-border flex items-center gap-3 border-b px-4 py-3">
        <span className="bg-primary text-primary-foreground flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold" aria-hidden="true">
          J
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-foreground text-sm font-semibold">Juni</h2>
          <p className="text-muted-foreground truncate text-xs">
            {audience === 'customer' ? 'Answers about Techie Gain and your account' : 'Answers about Techie Gain'}
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="size-8" onClick={startOver} disabled={messages.length === 0} aria-label="Start a new conversation" title="New conversation">
          <RotateCcw />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="size-8" onClick={() => setOpen(audience, false)} aria-label="Close Juni" title="Close">
          <X />
        </Button>
      </header>

      <div
        ref={scroller}
        onScroll={(event) => {
          const element = event.currentTarget;
          following.current = element.scrollHeight - element.scrollTop - element.clientHeight < 48;
        }}
        className="flex-1 space-y-4 overflow-y-auto px-4 py-4"
      >
        {messages.length === 0 ? (
          <div className="space-y-4">
            <p className="text-foreground text-sm leading-relaxed">
              Hi, I am Juni. {INTRO[audience]}
            </p>
            <div className="flex flex-col items-start gap-2">
              {SUGGESTIONS[audience].map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => void send(suggestion)}
                  className="border-border text-foreground hover:border-primary/60 hover:bg-muted/40 rounded-full border px-3 py-1.5 text-left text-sm transition"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message) =>
            message.role === 'user' ? (
              <div key={message.id} className="flex justify-end">
                <p className="bg-primary/15 text-foreground max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 text-sm leading-relaxed break-words whitespace-pre-wrap">
                  {message.text}
                </p>
              </div>
            ) : (
              <div key={message.id} className="bg-muted/40 rounded-2xl rounded-bl-sm px-3.5 py-3">
                <Answer message={message} />
              </div>
            ),
          )
        )}
      </div>

      <form onSubmit={submit} className="border-border space-y-2 border-t px-4 pt-3 pb-3">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor={inputId} className="text-xs">
            Your question
          </Label>
          {remaining < 200 && <span className={cn('text-xs', remaining < 0 ? 'text-danger' : 'text-muted-foreground')}>{remaining} characters left</span>}
        </div>
        <div className="flex items-end gap-2">
          <Textarea
            id={inputId}
            ref={input}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            maxLength={MAX_QUESTION_CHARS}
            rows={2}
            className="max-h-40 min-h-[2.75rem] flex-1 resize-none"
          />
          {busy ? (
            <Button type="button" size="icon" variant="outline" onClick={() => running.get(audience)?.abort()} aria-label="Stop the answer" title="Stop">
              <Square />
            </Button>
          ) : (
            <Button type="submit" size="icon" disabled={draft.trim() === ''} aria-label="Send the question" title="Send">
              <ArrowUp />
            </Button>
          )}
        </div>
        <p className="text-muted-foreground text-[11px] leading-snug">
          Juni can be wrong: check the Docs before changing a server. Never share passwords or keys.
        </p>
      </form>
    </section>
  );
}
